/**
 * rp-journal-trim — Journal cards cover their frame even when the upload is
 * padded. U19.
 *
 * Client, 2026-09-28: "on journal let's cover the card images so there are no
 * bars left and right". The cards already use `object-fit: cover`. The bars are
 * IN THE FILES: most featured images are portrait photos padded to a 1:1 square
 * with white side bars, for the previous theme's square thumbnails. Of 78
 * featured images checked on 2026-09-28, 76 are square to within 2% and 46 of
 * those carry light, symmetric side bars. Inside the padding the photo is 0.57 to 0.89 of the
 * width (median 0.67, a 2:3 portrait). A cover fit crops a square to 4:5, which
 * removes less than the padding, so a strip of it stays on each side.
 *
 * The same holds on the other axis: 4 landscape photos are padded to a square
 * with white bands top and bottom (about 17% each), and 2 carry padding on all
 * four sides.
 *
 * WHAT IT DOES, AND NOTHING ELSE:
 *   - loads a 200px rendition of each flagged image from the CDN (~10 KB)
 *   - counts the flat, light columns at the left and right edges and the flat,
 *     light rows at the top and bottom of that rendition
 *   - writes the photo's share of the width and height to `--rp-content-w` and
 *     `--rp-content-h` on the card image, and `--rp-origin: 50%` when the top and
 *     bottom are padded, so the zoom centres on the photo instead of the top band
 *
 * The zoom itself is CSS (`src/u9-journal.css`), because only the stylesheet
 * knows each rank's frame ratio and it stays right at every width without a
 * resize listener here.
 *
 * ⚠️ WHY A SEPARATE SMALL RENDITION, NOT THE CARD IMAGE ITSELF. Drawing the
 * displayed image into a canvas decodes it again ON THE MAIN THREAD: measured
 * 5-25 ms per card on desktop (2026-09-28), several times that on a phone.
 * `img.decode()` first did not help, `createImageBitmap(img)` decodes
 * synchronously too, and `fetch()`ing the same URL for an off-thread blob decode
 * downloaded the full file again (up to 2.8 MB). A 200px rendition decodes in
 * about 1 ms.
 *
 * ⚠️ THE 3% MARGIN IS MEASURED, NOT A GUESS. The CDN's resize softens the seam
 * between padding and photo, so a small rendition reads the photo slightly wider
 * than it is. Checked on all 76 square featured images against 800px renditions,
 * side padding (U19's first pass): both widths agree on which images are padded
 * (76/76); at 200px the reading ran
 * up to 0.5% of the width too wide on two images with a 2% margin, which leaves
 * a sliver of padding, and none with 3%. The cost of 3% is a slightly tighter
 * crop: 4.3% zoom beyond the exact figure at the median, 6.3% at most.
 * 100px renditions were rejected: up to 1.3% too wide even after a 2% margin,
 * which is a visible sliver on the wide feature card.
 *
 * WHEN: an eager card (the first screen) is sampled as soon as this module runs,
 * so the zoom is usually ready before its photo paints. A lazy card is sampled
 * when its own photo loads, which the browser starts well before the card
 * scrolls into view, so the sample only costs bytes for cards that are shown.
 *
 * `snippets/journal-card.liquid` flags square images only (`data-rp-trim="square"`).
 * Everything else is left alone:
 *   - bars that are not light: two illustrated maps with coloured margins and a
 *     flyer are designed that way, and a dark night sky is a photograph;
 *   - padding on one side only, or uneven by more than 4%: not padding;
 *   - a canvas that cannot be read (a cross-origin image in the theme editor):
 *     the card keeps its plain cover fit.
 *
 * No observer and no resize listener. Without this module the cards render
 * exactly as they did before U19.
 */

const SAMPLE = 200; // px wide
const MARGIN = 0.03; // share of each axis; see the measurement above
const LIGHT = 225; // every channel of a padding pixel is at least this bright
const FLAT = 18; // max spread per channel along one padding line (JPEG noise)

/** Is every pixel on this line light, and the line flat? `step` walks the line. */
function isPadding(data, start, step, count) {
  const lo = [255, 255, 255];
  const hi = [0, 0, 0];
  for (let k = 0; k < count; k += 2) {
    const i = (start + k * step) * 4;
    for (let c = 0; c < 3; c++) {
      const v = data[i + c];
      if (v < LIGHT) return false;
      if (v < lo[c]) lo[c] = v;
      if (v > hi[c]) hi[c] = v;
    }
  }
  return hi[0] - lo[0] <= FLAT && hi[1] - lo[1] <= FLAT && hi[2] - lo[2] <= FLAT;
}

/** Padding on both ends of one axis: at least 2% each, and even within 4%. */
const padded = (a, b, size) => a >= size * 0.02 && b >= size * 0.02 && Math.abs(a - b) <= size * 0.04;

/** The photo's share of the sample on each axis, or null when it is not padded. */
function photoBox(sample) {
  const width = sample.naturalWidth;
  const height = sample.naturalHeight;
  if (!width || !height) return null;

  let data;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(sample, 0, 0);
    data = ctx.getImageData(0, 0, width, height).data;
  } catch {
    return null; // tainted canvas: keep the plain cover fit
  }

  const column = (x) => isPadding(data, x, width, height);
  const row = (y) => isPadding(data, y * width, 1, width);
  const count = (test, size, from, dir) => {
    let n = 0;
    while (n < size / 2 && test(from + n * dir)) n++;
    return n;
  };
  const left = count(column, width, 0, 1);
  const right = count(column, width, width - 1, -1);
  const top = count(row, height, 0, 1);
  const bottom = count(row, height, height - 1, -1);

  const sides = padded(left, right, width);
  const bands = padded(top, bottom, height);
  if (!sides && !bands) return null;
  return {
    w: sides ? (width - left - right) / width - MARGIN : 1,
    h: bands ? (height - top - bottom) / height - MARGIN : 1,
    centre: bands,
  };
}

function trim(img) {
  const src = img.getAttribute('src');
  if (!src) return;
  const url = new URL(src, location.href);
  url.searchParams.set('width', String(SAMPLE));

  const sample = new Image();
  sample.decoding = 'async';
  sample.fetchPriority = 'low';
  sample.addEventListener(
    'load',
    () => {
      const box = photoBox(sample);
      if (!box) return;
      img.style.setProperty('--rp-content-w', box.w.toFixed(4));
      img.style.setProperty('--rp-content-h', box.h.toFixed(4));
      if (box.centre) img.style.setProperty('--rp-origin', '50%');
    },
    { once: true }
  );
  sample.src = url.href;
}

for (const img of document.querySelectorAll('img[data-rp-trim="square"]')) {
  if (img.loading !== 'lazy' || img.complete) trim(img);
  else img.addEventListener('load', () => trim(img), { once: true });
}
